import { Box } from '@mui/material';
import { memo } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Shared markdown renderer for Andie chat content.
 *
 * Uses remark-gfm to support advanced GitHub-flavoured markdown (tables,
 * strikethrough, task lists, autolinks) on top of the base react-markdown
 * elements, and lightly styles the richer elements so they render nicely
 * inside the MUI Typography wrapper used across the chat.
 */
const andieRemarkPlugins = [remarkGfm];

const andieMarkdownComponents: Components = {
    p: ({ children }) => (
        <>
            {children}
            <br />
        </>
    ),
    a: ({ children, href, ...props }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
        </a>
    ),
    table: ({ children }) => (
        <Box
            sx={{ display: 'block', maxWidth: '100%', overflowX: 'auto', my: 1, containerType: 'inline-size' }}
        >
            <Box
                component="table"
                sx={{
                    borderCollapse: 'collapse',
                    width: 'max-content',
                    maxWidth: 'none',
                    minWidth: '100%',
                    fontSize: '0.85em',
                    '& th, & td': {
                        border: '1px solid',
                        borderColor: 'divider',
                        px: 1,
                        py: 0.5,
                        textAlign: 'left',
                        verticalAlign: 'top',
                        // Nenhuma coluna passa de metade da largura visível; texto longo faz wrap.
                        maxWidth: '50cqw',
                        overflowWrap: 'break-word',
                    },
                    '& th': {
                        fontWeight: 600,
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                {children}
            </Box>
        </Box>
    ),
    code: ({ children }) => (
        <Box
            component="code"
            sx={{
                fontFamily: 'monospace',
                fontSize: '0.85em',
                backgroundColor: 'action.hover',
                px: 0.5,
                py: 0.125,
                borderRadius: 0.5,
            }}
        >
            {children}
        </Box>
    ),
    pre: ({ children }) => (
        <Box
            component="pre"
            sx={{
                backgroundColor: 'action.hover',
                p: 1.5,
                borderRadius: 1,
                overflowX: 'auto',
                my: 1,
                '& code': {
                    backgroundColor: 'transparent',
                    p: 0,
                },
            }}
        >
            {children}
        </Box>
    ),
    ul: ({ children }) => (
        <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
            {children}
        </Box>
    ),
    ol: ({ children }) => (
        <Box component="ol" sx={{ pl: 2.5, my: 0.5 }}>
            {children}
        </Box>
    ),
    blockquote: ({ children }) => (
        <Box
            component="blockquote"
            sx={{
                borderLeft: '3px solid',
                borderColor: 'divider',
                pl: 1.5,
                my: 1,
                color: 'text.secondary',
            }}
        >
            {children}
        </Box>
    ),
};

const AndieMarkdown = memo(({ text }: { text: string }) => (
    <Markdown remarkPlugins={andieRemarkPlugins} components={andieMarkdownComponents}>
        {text}
    </Markdown>
));

AndieMarkdown.displayName = 'AndieMarkdown';

export default AndieMarkdown;
export { andieMarkdownComponents, andieRemarkPlugins };
